import { useRef, useState } from 'react';
import type { Branding, GeneratorConfigV1, Locale, Currency } from '../../types/generator-config';
import { useUploadLogo } from '../../hooks/useUploadLogo';

interface BrandingSectionProps {
    branding: Branding;
    organizationId: string;
    generatorId: string;
    onChange: (updater: (prev: GeneratorConfigV1) => GeneratorConfigV1) => void;
}

const PRESET_COLORS = [
    '#FF8660', '#14B8A6', '#0EA5E9', '#3B82F6',
    '#8B5CF6', '#EC4899', '#F59E0B', '#22C55E',
    '#EF4444', '#0F172A',
];

export function BrandingSection({
    branding,
    organizationId,
    generatorId,
    onChange,
}: BrandingSectionProps) {
    const { uploading, error: uploadError, upload } = useUploadLogo();
    const [localError, setLocalError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const set = <K extends keyof Branding>(key: K, value: Branding[K]) => {
        onChange((prev) => ({ ...prev, branding: { ...prev.branding, [key]: value } }));
    };

    const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setLocalError(null);
        try {
            const url = await upload(file, { organizationId, generatorId });
            set('logoUrl', url);
        } catch (err) {
            setLocalError((err as Error).message);
        }
    };

    const handleRemoveLogo = () => {
        set('logoUrl', null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    return (
        <div className="editor-fields">
                <label className="field">
                    <span className="field-label">Nom affiché</span>
                    <input
                        type="text"
                        value={branding.companyName}
                        maxLength={80}
                        onChange={(e) => set('companyName', e.target.value)}
                        placeholder="DB-BAT, Climatisation Dupont..."
                    />
                </label>

                <div className="field">
                    <span className="field-label">Couleur d'accent</span>
                    <div className="color-row">
                        <input
                            type="color"
                            value={branding.primaryColor}
                            onChange={(e) => set('primaryColor', e.target.value)}
                            className="color-input"
                            aria-label="Sélecteur de couleur"
                        />
                        <input
                            type="text"
                            value={branding.primaryColor}
                            onChange={(e) => set('primaryColor', e.target.value)}
                            className="color-hex"
                            maxLength={7}
                            pattern="^#[0-9A-Fa-f]{6}$"
                        />
                    </div>
                    <div className="color-presets">
                        {PRESET_COLORS.map((c) => (
                            <button
                                key={c}
                                type="button"
                                className={`color-preset${branding.primaryColor.toLowerCase() === c.toLowerCase() ? ' is-active' : ''}`}
                                style={{ background: c }}
                                onClick={() => set('primaryColor', c)}
                                aria-label={`Couleur ${c}`}
                            />
                        ))}
                    </div>
                </div>

                <div className="field">
                    <span className="field-label">Logo</span>
                    <div className="logo-upload">
                        {branding.logoUrl ? (
                            <div className="logo-preview">
                                <img src={branding.logoUrl} alt="Logo" />
                                <div className="logo-preview-actions">
                                    <button
                                        type="button"
                                        className="btn-ghost"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploading}
                                    >
                                        Remplacer
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-ghost btn-ghost-danger"
                                        onClick={handleRemoveLogo}
                                        disabled={uploading}
                                    >
                                        Retirer
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button
                                type="button"
                                className="logo-drop"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="17 8 12 3 7 8" />
                                    <line x1="12" y1="3" x2="12" y2="15" />
                                </svg>
                                <span>
                                    {uploading ? 'Upload en cours…' : 'Cliquez pour uploader un logo'}
                                </span>
                                <small>PNG, JPG, WebP ou SVG · 5 MB max</small>
                            </button>
                        )}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/svg+xml"
                            onChange={handleLogoChange}
                            style={{ display: 'none' }}
                        />
                        {(uploadError || localError) && (
                            <div className="auth-error" role="alert">
                                {uploadError ?? localError}
                            </div>
                        )}
                    </div>
                </div>

                <div className="fields-row">
                    <label className="field">
                        <span className="field-label">Langue par défaut</span>
                        <select
                            value={branding.locale}
                            onChange={(e) => set('locale', e.target.value as Locale)}
                        >
                            <option value="fr">Français</option>
                            <option value="en">English</option>
                        </select>
                    </label>

                    <label className="field">
                        <span className="field-label">Devise</span>
                        <select
                            value={branding.currency}
                            onChange={(e) => set('currency', e.target.value as Currency)}
                        >
                            <option value="EUR">EUR (€)</option>
                            <option value="USD">USD ($)</option>
                            <option value="CAD">CAD ($)</option>
                        </select>
                    </label>
                </div>
        </div>
    );
}
